import React, { useEffect, useState } from 'react';
import { Button, Container, Modal } from 'react-bootstrap';
import CommonReactTable from '../../CommonUtils/CommonReactTable';
import { CommonAxiosGet } from '../../CommonUtils/CommonAxios';
import { config } from '../../CommonUtils/CommonURLS';
import CommonCardHeader from '../../CommonUtils/CommonCardHeader';
import { FaEye } from 'react-icons/fa';
import StudentStatusReport from './StudentStatusReport';

function formatReportDate(dateValue) {
    if (!dateValue) {
        return '-';
    }
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return dateValue;
    }

    return parsedDate.toLocaleDateString('en-GB');
}

function getAcademicProgress(studentData) {
    const presentYear = studentData?.present_ac_year;
    const presentSemester = studentData?.present_semester;
    const nextYear = studentData?.next_ac_year;
    const nextSemester = studentData?.next_semester;

    const hasAnyValue = presentYear || presentSemester || nextYear || nextSemester;
    if (!hasAnyValue) {
        return '-';
    }
    if (!presentSemester && !nextSemester) {
        return `${presentYear || '-'} -> ${nextYear || '-'}`;
    }
    return `${presentYear || '-'} / ${presentSemester || '-'} -> ${nextYear || '-'} / ${nextSemester || '-'}`;
}

export default function StudentOverallStatusReport() {
    const [countReport, setcountReport] = useState([]);
    const [detailRows, setDetailRows] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedStudentId, setSelectedStudentId] = useState(null);

    useEffect(() => {
        const loadStudentStatusRows = async () => {
            setLoading(true);
            try {
                const res = await CommonAxiosGet(`${config.url.STU_URL}getstudentstatusreportbydate`);
                if (res.data.scode === "01") {
                    const dateRows = res?.data?.student_report_date || [];
                    setcountReport(Array.isArray(dateRows) ? dateRows : []);
                } else {
                    setcountReport([]);
                    swal.fire("Error", res.data.sdesc, "error");
                }
            } catch (error) {
                console.error('Error loading student overall status report:', error);
                setcountReport([]);
            } finally {
                setLoading(false);
            }
        };

        loadStudentStatusRows();
    }, []);

    const handleOpenDetails = async (dateValue) => {
        setSelectedDate(dateValue);
        setDetailLoading(true);
        try {
            const encodedDate = encodeURIComponent(dateValue);
            const drillUrl = `${config.url.STU_URL}getstudentstatusreport/${encodedDate}`;
            const res = await CommonAxiosGet(drillUrl);
            if (res.data.scode === "01") {
                const rows = res?.data?.student_report
                setDetailRows(Array.isArray(rows) ? rows : []);
            } else {
                setDetailRows([]);
                swal.fire("Error", res.data.sdesc, "error");
            }
        } catch (error) {
            console.error('Error loading student details report:', error);
            setDetailRows([]);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleBack = () => {
        setSelectedDate(null);
        setDetailRows([]);
    };

    const handleOpenStatusModal = (rowData) => {
        const studentId = rowData?.student_reg_no || rowData?.user_id || rowData?.candidate_id;
        if (!studentId) {
            return;
        }
        setSelectedStudentId(studentId);
        setShowStatusModal(true);
    };

    const handleCloseStatusModal = () => {
        setShowStatusModal(false);
        setSelectedStudentId(null);
    };

    const summaryColumns = [
        {
            id: 'slNo',
            header: 'S.No',
            cell: ({ row }) => row.index + 1,
        },
        {
            id: 'insert_date',
            header: 'Report Date',
            accessorKey: 'insert_date',
            Footer: 'Total',
            meta: { style: { textAlign: 'center' } },
            cell: ({ row }) => formatReportDate(row.original.insert_date),
        },
        {
            id: 'count',
            header: 'Count',
            accessorKey: 'count',
            Footer: true,
            meta: { style: { textAlign: 'center' } },
            cell: ({ row }) => (
                <button
                    className="status-drill-link"
                    onClick={() => handleOpenDetails(row.original.insert_date)}
                    disabled={detailLoading}
                >
                    {row.original.count ?? 0}
                </button>
            ),
        },
    ];

    const detailColumns = [
        {
            id: 'slNo',
            header: 'S.No',
            cell: ({ row }) => row.index + 1,
        },
        {
            id: 'student_reg_no',
            header: 'Student ID',
            accessorKey: 'student_reg_no',
            cell: ({ row }) => row.original.student_reg_no || row.original.candidate_id || '-',
        },
        {
            id: 'student_name',
            header: 'Student Name',
            accessorKey: 'student_name',
            cell: ({ row }) => row.original.student_name || row.original.applicant_name || '-',
        },
        {
            id: 'gender',
            header: 'Gender',
            accessorKey: 'gender',
            cell: ({ row }) => row.original.gender || '-',
        },
        {
            id: 'mobile_no',
            header: 'Mobile No',
            accessorKey: 'mobile_no',
            cell: ({ row }) => row.original.mobile_no || row.original.stu_mobile || '-',
        },
        {
            id: 'course_name',
            header: 'Course',
            accessorKey: 'course_name',
            cell: ({ row }) => row.original.course_name || '-',
        },
        {
            id: 'prsent_collname',
            header: 'Present College',
            accessorKey: 'prsent_collname',
            cell: ({ row }) => row.original.prsent_collname || '-',
        },
        {
            id: 'trans_collname',
            header: 'Transfer College',
            accessorKey: 'trans_collname',
            cell: ({ row }) => row.original.trans_collname || '-',
        },
        {
            id: 'transfer_reason',
            header: 'Transfer Reason',
            accessorKey: 'transfer_reason',
            cell: ({ row }) => row.original.transfer_reason || '-',
        },
        {
            id: 'academic_progress',
            header: 'Academic Progress',
            cell: ({ row }) => getAcademicProgress(row.original),
        },
        {
            id: 'ac_year',
            header: 'Academic Year',
            accessorKey: 'ac_year',
            cell: ({ row }) => row.original.ac_year || '-',
        },
        {
            id: 'overall_status',
            header: 'Status',
            accessorKey: 'overall_status',
            meta: { style: { textAlign: 'center' } },
            cell: ({ row }) => {
                const rowStudentId = row.original?.student_reg_no || row.original?.user_id || row.original?.candidate_id;
                return (
                    <div className="text-center">
                        <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleOpenStatusModal(row.original)}
                        >
                            <FaEye />
                        </Button>
                    </div>
                );
            },
        }
    ];

    return (
        <Container fluid className="py-4 ">
            <Modal
                show={showStatusModal}
                onHide={handleCloseStatusModal}
                size="lg"
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Student Status Report - {selectedStudentId}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', backgroundColor: '#e9f3fc', padding: '20px' }}>
                    {selectedStudentId && <StudentStatusReport studentId={selectedStudentId} />}
                </Modal.Body>
            </Modal>

            <section className="workflow-section-surface status-report-shell">
                <CommonCardHeader
                    title={selectedDate ? `Student Details Report - ${formatReportDate(selectedDate)}` : 'Student Overall Status Report'}
                    onBack={selectedDate ? handleBack : undefined}
                    backLabel="Back"
                />

                <div className="workflow-section-body">
                    {loading ? (
                        <div className="status-report-empty">Loading...</div>
                    ) : selectedDate ? (
                        detailLoading ? (
                            <div className="status-report-empty">Loading selected date details...</div>
                        ) : detailRows.length > 0 ? (
                            <>
                                <div className="status-report-table-wrap">
                                    <CommonReactTable
                                        data={detailRows}
                                        columns={detailColumns}
                                        filename={`StudentOverallStatusReport_${selectedDate}`}
                                        headerName={`Student Details Report - ${formatReportDate(selectedDate)}`}
                                        showFooter={false}
                                        showPagination={false}
                                        showExcel={true}
                                        showPrint={true}
                                        excelFileName={`Student_Details_Report_${selectedDate}`}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="d-flex justify-content-end mb-3">
                                    <Button variant="secondary" size="sm" onClick={handleBack}>
                                        Back to Date Summary
                                    </Button>
                                </div>
                                <div className="status-report-empty">No data found for selected date.</div>
                            </>
                        )
                    ) : countReport.length > 0 ? (
                        <div className="status-report-table-wrap status-report-table-wrap--compact">
                            <CommonReactTable
                                data={countReport}
                                columns={summaryColumns}
                                filename="StudentOverallStatusReport"
                                headerName="Student Overall Status Report"
                                showFooter={true}
                                showPagination={false}
                                showExcel={true}
                                showPrint={true}
                                excelFileName="Student_Overall_Status_Report"
                            />
                        </div>
                    ) : (
                        <div className="status-report-empty">No date summary found.</div>
                    )}
                </div>
            </section>
        </Container>
    );
}
